import { Chip, CircularProgress, Fade, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { LocationValue, Rule } from "../../../App/Typings/DynamicGroupRules";
import { FieldType } from "../../../DynamicGroup/DynamicGroup";
import { dateCommandTranslateMap, labels } from "../../../DynamicGroup/Components/DynamicFilterRules";
import { ICampaignFormState } from "../../../App/Typings/Campaigns";
import { IGroup } from "../../../App/Typings/Groups";
import { useCandidateStatusDefinitionsQuery } from "../../../App/Queries/CandidateStatusQueries";
import { CANDIDATE_STATUS_DEFAULTS } from "../../../App/Components/CandidateStatus";
import { ICandidateStatusDefinition } from "../../../App/Typings/CandidateStatus";

export function DisplayRecursiveFilterChips({
  displayRules,
  formFields,
  handleRemoveRule,
  forms,
  groups,
  isLoading,
}: {
  displayRules: Rule[];
  formFields: FieldType[];
  handleRemoveRule: (ruleId: string) => void;
  forms: ICampaignFormState[];
  groups: IGroup[];
  isLoading: boolean;
}) {
  const pendingRulesToFieldMap = displayRules.reduce(
    (acc, rule) => {
      processRule(rule, acc, formFields);
      return acc;
    },
    {} as Record<string, FieldType>,
  );

  const { data: statusDefinitions } = useCandidateStatusDefinitionsQuery();

  return isLoading && displayRules.length > 0 ? (
    <Stack direction="row" spacing={1} alignItems={"center"}>
      <CircularProgress size={12} />
      <Typography>Loading filters</Typography>
    </Stack>
  ) : (
    <Fade in timeout={1000}>
      <Stack direction="row" spacing={1}>
        {displayRules.map((rule, index) => {
          const label = createChipLabel(rule, pendingRulesToFieldMap, forms, groups, statusDefinitions);
          return (
            <Chip
              data-test={`filter-chip-${index}`}
              key={rule.id}
              sx={{ marginLeft: 1 }}
              size="medium"
              color="primary"
              label={label}
              onDelete={() => handleRemoveRule(rule.id)}
            />
          );
        })}
      </Stack>
    </Fade>
  );
}

function processRule(rule: Rule, acc: Record<string, FieldType>, formFields: FieldType[]) {
  const hasAndRules = rule.rules && rule.rules.length > 0;
  if (hasAndRules) {
    rule.rules.forEach((nestedRule) => {
      processRule(nestedRule, acc, formFields);
    });
  }

  const fieldConfig = formFields.find(
    (f) =>
      f.id.includes(rule.filter.field) || (rule.filter.type === "datetime" && f.field.fieldType === "date"),
  );
  if (fieldConfig) {
    acc[rule.filter.field] = fieldConfig;
  }
}

const createChipLabel = (
  rule: Rule,
  rulesFieldMap: Record<string, FieldType>,
  forms: ICampaignFormState[],
  groups: IGroup[],
  statusDefinitions: ICandidateStatusDefinition[] | undefined,
) => {
  let command = labels.get(rule.filter.command) ?? rule.filter.command;
  let displayValue = rule.filter.value.value;

  if (rule.filter.type === "interestpicker" || rule.filter.type === "array") {
    const arrayValues = rule.filter.value.value.split(",").map((c: string) => decodeURIComponent(c));

    if (rule.filter.field === "campaignResponses.campaignId") {
      displayValue = forms
        .filter((c) => arrayValues.includes(c.campaignId))
        .map((c) => c?.campaignName ?? "")
        .join(", ");
    } else if (rule.filter.field === "groupIds") {
      displayValue = groups
        .filter((g) => arrayValues.includes(g.id))
        .map((c) => c?.name ?? "")
        .join(", ");
    } else if (rule.filter.field === "statusColor") {
      const source = statusDefinitions?.length ? statusDefinitions : CANDIDATE_STATUS_DEFAULTS;
      displayValue = arrayValues
        .map((color: string) => source.find((d) => d.color === color)?.label ?? color)
        .join(", ");
    } else {
      displayValue = arrayValues.join(", ");
    }
  } else if (rule.filter.type === "datetime") {
    command = dateCommandTranslateMap.get(rule.filter.command) ?? command;
    if (rule.filter.command === "between") {
      const startDate = dayjs(rule.filter.value.value.item1).format("M/DD/YYYY");
      const endDate = dayjs(rule.filter.value.value.item2).format("M/DD/YYYY");
      displayValue = `${startDate} and ${endDate}`;
    } else {
      displayValue = dayjs(rule.filter.value.value).format("MM/DD/YYYY");
    }
  }

  const baseLabel = `${
    rulesFieldMap[rule.filter.field]?.field?.label ??
    rulesFieldMap[rule.filter.field]?.field?.fieldName ??
    candidateFields.get(rule.filter.field)
  } ${command}`;

  let label = (
    <>
      {baseLabel} <strong>"{displayValue}"</strong>
    </>
  );

  if (rule.filter.type === "location") {
    const location = rule.filter.value as LocationValue;
    const displayCommand = command.charAt(0).toUpperCase() + command.slice(1);
    label = (
      <>
        {displayCommand} <strong>{location.radius} miles</strong> from <strong>{location.name}</strong>
      </>
    );
  }

  if (rule.rules && rule.rules.length > 0) {
    // Add the "+1" for each nested "and"
    label = (
      <>
        {label} +{rule.rules.length} more
      </>
    );
  }
  return label;
};

const candidateFields = new Map([
  ["campaignResponses.campaignId", "Form"],
  ["groupIds", "Group"],
  ["firstName", "First Name"],
  ["lastName", "Last Name"],
  ["emailAddresses.emailAddress", "Email Address"],
  ["phoneNumbers.phoneNumber", "Phone Number"],
  ["createdDate", "Date Added"],
  ["geolocation", "Geolocation"],
  ["tags", "Tags"],
  ["statusColor", "Status"],
]);
